import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { attentionItems, withoutDismissed, type AttentionItem, type AttentionTone } from '@/lib/attention'
import {
  useAnalyticsStatus,
  useAttentionDismissals,
  useDismissAttention,
  usePlaySlots,
  useTrackingStatus,
  useWatchedRuns,
} from '@/lib/queries'
import { cn } from '@/lib/utils'
import { errorText } from './ErrorState'
import { StatusDot } from './StatusPill'
import { useToast } from './Toast'
import { When } from './When'

/** The band under every page header that lists what is broken right now: a
 *  rank snapshot that failed or is overdue, the analytics pull failing, Play
 *  slots locked or the last attempt refused, a scheduled job whose last run
 *  failed. When nothing is wrong it is not drawn at all.
 *
 *  Fed by the tracker's and the collector's status, the Play slot summary and
 *  the last 50 job runs — polled every 30 seconds through the shared query
 *  cache. An item cleared by hand (자세히 → 지우기) stays out until the same
 *  thing happens again. */
export function AttentionStrip({ className }: { className?: string }) {
  return <AttentionList items={useAttentionItems()} onDismiss={useDismissItem()} className={className} />
}

/** Clears one item by hand, saying so if the save fails (the item comes
 *  back). */
export function useDismissItem(): (item: Pick<AttentionItem, 'key' | 'stamp'>) => void {
  const dismiss = useDismissAttention()
  const toast = useToast()
  return (item) => {
    if (item.stamp === undefined) return
    dismiss.mutate(
      { key: item.key, stamp: item.stamp },
      { onError: (e) => toast.error(`지우지 못했다: ${errorText(e)}`) },
    )
  }
}

/** The strip's items, for a page that shows them its own way (오늘 folds
 *  them into its "확인할 것" list instead of drawing the strip) and for the
 *  오늘 badge. */
export function useAttentionItems(): AttentionItem[] {
  const tracking = useTrackingStatus()
  const analytics = useAnalyticsStatus(true)
  const slots = usePlaySlots()
  const runs = useWatchedRuns()
  const dismissals = useAttentionDismissals()
  return useMemo(() => {
    const list = withoutDismissed(
      attentionItems({
        tracking: tracking.data,
        analytics: analytics.data,
        playSlots: slots.data?.summary,
        runs: runs.runs,
      }),
      dismissals.data?.items,
    )
    if (tracking.error) {
      list.unshift({
        key: 'tracking-status-error',
        tone: 'failed',
        label: '스냅샷 상태를 읽지 못했다',
        count: 0,
        to: '/',
        details: [{ where: 'GET /api/tracking/status', message: errorText(tracking.error) }],
      })
    }
    return list
  }, [tracking.data, tracking.error, analytics.data, slots.data, runs.runs, dismissals.data])
}

function worst(items: AttentionItem[]): AttentionTone {
  if (items.some((i) => i.tone === 'failed')) return 'failed'
  if (items.some((i) => i.tone === 'publishing')) return 'publishing'
  return 'neutral'
}

const BAR: Record<AttentionTone, string> = {
  failed: 'bg-status-failed',
  publishing: 'bg-status-publishing',
  neutral: 'bg-status-neutral',
}

const TITLE: Record<AttentionTone, string> = {
  failed: 'text-status-failed',
  publishing: 'text-status-publishing',
  neutral: 'text-foreground',
}

/** The strip's drawing, apart from its data — tests render this directly.
 *  With onDismiss, each item that can be cleared gets 지우기 in 자세히. */
export function AttentionList({
  items,
  onDismiss,
  className,
}: {
  items: AttentionItem[]
  onDismiss?: (item: AttentionItem) => void
  className?: string
}) {
  const [open, setOpen] = useState(false)
  if (items.length === 0) return null
  const tone = worst(items)
  return (
    <section
      aria-label="확인할 것"
      className={cn('relative overflow-hidden rounded-lg border border-border bg-card pl-1', className)}
    >
      <span
        aria-hidden
        className={cn('absolute inset-y-0 left-0 w-1', BAR[tone])}
      />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2">
        <h2
          className={cn(
            'order-1 flex shrink-0 items-center gap-2 text-table font-semibold',
            TITLE[tone],
          )}
        >
          <StatusDot tone={tone} />
          <span>
            확인할 것 <span className="tabular-nums">{items.length}</span>건
          </span>
        </h2>
        {/* One line on the phone, sideways if it has to: the strip is on every
            page and should not push the page down. */}
        <ul className="order-3 -mx-3 flex w-[calc(100%+1.5rem)] min-w-0 gap-1.5 overflow-x-auto px-3 [scrollbar-width:none] md:order-2 md:mx-0 md:w-auto md:flex-1 md:flex-wrap md:overflow-visible md:px-0 [&::-webkit-scrollbar]:hidden">
          {items.map((i) => (
            <li key={i.key} className="shrink-0">
              <Link
                to={i.to}
                className="inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-background px-2.5 text-meta font-medium text-foreground transition-colors hover:border-foreground/25 hover:bg-muted"
              >
                <StatusDot tone={i.tone} />
                {i.label}
                {i.count > 0 && <span className="tabular-nums text-muted-foreground">{i.count}</span>}
              </Link>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="order-2 ml-auto inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-meta font-medium text-muted-foreground hover:bg-muted hover:text-foreground md:order-3"
        >
          {open ? '접기' : '자세히'}
          <ChevronDown size={14} className={cn('transition-transform', open && 'rotate-180')} aria-hidden />
        </button>
      </div>
      {open && (
        <div className="space-y-3 border-t border-border bg-muted/30 px-3 py-3">
          {items.map((i) => (
            <div key={i.key} className="space-y-1.5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-table font-semibold">
                  {i.label}
                  {i.count > 0 && <span className="ml-1 tabular-nums text-muted-foreground">{i.count}</span>}
                </span>
                <span className="flex items-baseline gap-3">
                  {onDismiss && i.stamp !== undefined && (
                    <button
                      type="button"
                      onClick={() => onDismiss(i)}
                      aria-label={`${i.label} 지우기`}
                      title="목록에서 지운다. 같은 일이 다시 생기면 다시 뜬다. 작업 기록은 그대로 남는다."
                      className="text-meta font-medium text-muted-foreground hover:text-foreground hover:underline"
                    >
                      지우기
                    </button>
                  )}
                  <Link to={i.to} className="text-meta font-medium text-accent hover:underline">
                    열기
                  </Link>
                </span>
              </div>
              <ul className="space-y-1.5">
                {i.details.map((d, n) => (
                  <li key={n} className="text-meta">
                    <div className="flex flex-wrap items-baseline gap-x-2 text-muted-foreground">
                      <span className="font-medium text-foreground">{d.where}</span>
                      {d.what && <span className="min-w-0 truncate">{d.what}</span>}
                      {d.at && <When iso={d.at} className="ml-auto shrink-0" />}
                    </div>
                    {d.message && (
                      <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-muted px-2 py-1 font-mono text-micro text-foreground">
                        {d.message}
                      </pre>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
